import defaultTo from 'lodash/defaultTo'
import map from 'lodash/map'
import filter from 'lodash/filter'
import moment from 'moment'
import d3 from 'd3'

export default subscriptionTimelineGraph

/** @ngInject */
function subscriptionTimelineGraph($filter) {
    return ({
        restrict: 'E',
        scope: {
            data: '<',
            sleek_timetable: '<sleekTimetable',
            tutorial: '<?',
        },
        link($scope, element) {
            const xlgGraphSize = { width: 860, height: 360 }

            const containerWidth = xlgGraphSize.width
            const containerHeight = xlgGraphSize.height
            const margin = { top: 50, right: 5, bottom: 5, left: 5 }
            const width = containerWidth - margin.left - margin.right
            const height = containerHeight - margin.top - margin.bottom
            const viewBox = `0 0 ${containerWidth} ${containerHeight}`
            const transitionDuration = 750

            let chart = {}
            $scope.tutorial = defaultTo($scope.tutorial, {})

            const extendPadding = function (array, accessor, paddingPercent) {
                const extend = d3.extent(array, accessor)
                const range = extend[1] - extend[0]
                return [extend[0] - (range * paddingPercent), extend[1] + (range * paddingPercent)]
            }

            const seasonClass = function (d) {
                const date = moment(d.date)
                const isWinter = [11, 0, 1].indexOf(date.month()) > -1
                const isSummer = [5, 6, 7].indexOf(date.month()) > -1

                if (isWinter) {
                    return 'month-info-winter'
                } else if (isSummer) {
                    return 'month-info-summer'
                }
                return ''
            }

            const generateGraph = function () {
                if ($scope.sleek_timetable) {
                    element.addClass('averaged')
                } else {
                    element.removeClass('averaged')
                }

                const svg = d3.select(element.get(0))
          .append('svg')
          .attr('width', containerWidth)
          .attr('height', containerHeight)
          .attr('preserveAspectRatio', 'xMinYMin meet')
          .attr('viewBox', viewBox)

                const g = svg.append('g')
          .attr('transform', `translate(${margin.left},${margin.top})`)

                const x = d3.scale.ordinal()
          .domain(map($scope.data, (d) => d.date))
          .rangeBands([0, width], 0, 0)

                const y = d3.scale.linear()
          .domain(extendPadding($scope.data, ((d) => d.amount), 0.5))
          .rangeRound([height, 0])

        // Data positionning logic
                const amount = {
                    line: (d3.svg.line()
            .x((d) => x(d.date) + (x.rangeBand(d) / 2))
            .y((d) => y(d.amount))),
                    area: (d3.svg.area()
            .x((d) => x(d.date) + (x.rangeBand(d) / 2))
            .y0(height)
            .y1((d) => y(d.amount))),
                    label(d) {
                        return `${$filter('frAmount')(d.amount)} €`
                    },
                }
                const average = {
                    line: (d3.svg.line()
            .x((d) => x(d.date) + (x.rangeBand(d) / 2))
            .y((d) => y(d.sleeked))),
                    area: (d3.svg.area()
            .x((d) => x(d.date) + (x.rangeBand(d) / 2))
            .y0(height)
            .y1((d) => y(d.sleeked))),
                    label(d) {
                        return `${$filter('frAmount')(d.sleeked)} €`
                    },
                }

                const drawLogic = $scope.sleek_timetable ? average : amount
        // Background area (background of date column)
                g.append('path')
                .attr('class', 'graph-area')
                .attr('d', drawLogic.area($scope.data))

        // Date column
                g.selectAll('.month-delimiter')
                .data($scope.data)
                .enter().append('rect')
                .attr('class', (d) => {
                    let clazz = 'month-delimiter'
                    if (!d.amountDisplay) {
                        clazz += ' month-delimiter-no-amount'
                    }
                    if (d.nextReading) {
                        clazz += ' month-delimiter-reading'
                    }
                    return clazz
                })
                .attr('x', (d) => x(d.date))
                .attr('y', (d) => {
                    if (d.nextReading) {
                        return -1 * margin.top
                    }
                    return 0
                })
                .attr('width', 1)
                .attr('height', (d) => {
                    if (d.nextReading) {
                        return height + margin.top
                    }
                    return height
                })

        // Date label
                g.selectAll('.month-label')
                .data($scope.data)
                .enter().append('text')
                .attr('class', (d) => `month-label ${d.amountDisplay ? '' : 'month-label-no-amount'}`)
                .attr('x', (d) => x(d.date) + 10)
                .attr('y', 13)
                .text((d) => $filter('date')(d.date, 'MMM yy'))

        // Next Reading label
                g.selectAll('.month-reading-label-1')
                .data(filter($scope.data, (d) => d.nextReading))
                .enter().append('text')
                .attr('class', 'month-reading-label month-reading-label-1')
                .attr('x', (d) => x(d.date) + 10)
                .attr('y', -35)
                .text('Relève de')
                g.selectAll('.month-reading-label-2')
                .data(filter($scope.data, (d) => d.nextReading))
                .enter().append('text')
                .attr('class', 'month-reading-label month-reading-label-2')
                .attr('x', (d) => x(d.date) + 10)
                .attr('y', -20)
                .text('compteur Enedis')

        // Border left + bottom
                g.append('rect')
                .attr('class', 'border-left')
                .attr('x', 0)
                .attr('y', 0)
                .attr('width', 2)
                .attr('height', height)
                g.append('rect')
                .attr('class', 'border-bottom')
                .attr('x', 0)
                .attr('y', height)
                .attr('width', width)
                .attr('height', 2)

        // Line
                g.append('path')
                .attr('class', 'graph-line')
                .attr('d', drawLogic.line($scope.data))

        // Graph Dot
                g.selectAll('.graph-dot')
                .data($scope.data)
                .enter().append('circle')
                .attr('class', 'graph-dot')
                .attr('cx', drawLogic.line.x())
                .attr('cy', drawLogic.line.y())
                .attr('r', 8)

        // Amount
                g.selectAll('.graph-amount')
                .data(filter($scope.data, 'amountDisplay'))
                .enter().append('text')
                .attr('class', 'graph-amount')
                .attr('x', drawLogic.line.x())
                .attr('y', (d) => drawLogic.line.y()(d) - 30)
                .text(drawLogic.label)

        // Info column
                g.selectAll('.month-info')
                .data($scope.data)
                .enter().append('rect')
                .attr('class', (d) => `month-info ${seasonClass(d)}`)
                .attr('x', (d) => x(d.date))
                .attr('y', 0)
                .attr('width', x.rangeBand())
                .attr('height', height)

                return { svg, x, y, amount, average }
            }

            const resizeGraph = function () {
                const elementWidth = element.get(0).getBoundingClientRect().width > 1000 ? 1000 : element.get(0).getBoundingClientRect().width
                let graphSize = xlgGraphSize
                graphSize = { width: elementWidth, height: Math.round(elementWidth * (graphSize.height / graphSize.width)) }

                chart.svg.attr('width', graphSize.width)
                chart.svg.attr('height', graphSize.height)
            }

            const updateGraphOnDisplayAverage = function (sleek_timetable) {
                const svg = d3.select(element.get(0)).transition()
                const drawLogic = sleek_timetable ? chart.average : chart.amount

                if (sleek_timetable) {
                    element.addClass('averaged')
                } else {
                    element.removeClass('averaged')
                }

                svg.select('.graph-area')
                .duration(transitionDuration)
                .attr('d', drawLogic.area($scope.data))

                svg.select('.graph-line')
                .duration(transitionDuration)
                .attr('d', drawLogic.line($scope.data))

        // Graph Dot
                svg.selectAll('.graph-dot')
                .duration(transitionDuration)
                .attr('cx', drawLogic.line.x())
                .attr('cy', drawLogic.line.y())

        // Amount
                svg.selectAll('.graph-amount')
                .duration(transitionDuration)
                .attr('x', drawLogic.line.x())
                .attr('y', (d) => drawLogic.line.y()(d) - 30)
                .text(drawLogic.label)
            }

            const updateGraphOnTutorial = function (tutorial) {
                updateGraphOnDisplayAverage(tutorial.display ? tutorial.sleek_timetable : $scope.sleek_timetable)
                element[tutorial.isSummer ? 'addClass' : 'removeClass']('summer')
                element[tutorial.isWinter ? 'addClass' : 'removeClass']('winter')
                element[tutorial.isNextReading ? 'addClass' : 'removeClass']('next-reading')
            }

            const activate = function () {
                chart = generateGraph()
                resizeGraph()
                d3.select(window)
                .on('resize', resizeGraph)

                $scope.$watch('sleek_timetable', updateGraphOnDisplayAverage)
                $scope.$watch('tutorial', updateGraphOnTutorial)
            }

            activate()
        },
    })
}



// WEBPACK FOOTER //
// ./src/directives/subscriptionTimelineGraph/subscriptionTimelineGraph.js